import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Award, Calendar, Mail, MapPin, User, Crown, IdCard, Download, ExternalLink, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const MyProfile = () => {
  const { user, API, logout } = useAuth();
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idNumber, setIdNumber] = useState(user?.id_number || "");
  const [savingId, setSavingId] = useState(false);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchCertificates();
  }, []); // eslint-disable-line

  useEffect(() => {
    setIdNumber(user?.id_number || "");
  }, [user]);

  const fetchCertificates = async () => {
    try {
      const response = await axios.get(`${API}/my-certificates`);
      setCertificates(response.data);
    } catch (error) {
      console.error("Error fetching certificates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveIdNumber = async () => {
    if (!idNumber || idNumber.trim().length < 5) {
      toast.error("Ingresa una cédula o pasaporte válido (mínimo 5 caracteres)");
      return;
    }
    setSavingId(true);
    try {
      await axios.put(`${API}/auth/me`, { id_number: idNumber.trim() });
      toast.success("Cédula/Pasaporte guardado");
      // refrescar usuario
      window.location.reload();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSavingId(false);
    }
  };

  const handleDownloadCertificate = async (cert) => {
    if (!user?.id_number && !idNumber) {
      toast.error("Primero registra tu cédula/pasaporte arriba");
      return;
    }
    setDownloading(cert.course_id);
    try {
      const response = await axios.get(`${API}/certificate/${cert.course_id}`, {
        responseType: "blob",
      });
      const blob = response.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificado_${cert.code || cert.course_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Certificado descargado");
      fetchCertificates();
    } catch (e) {
      let msg = "No se pudo descargar el certificado";
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch (parseErr) {
          // ignore
        }
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setDownloading(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("¿Estás seguro de que deseas cancelar tu suscripción?")) {
      return;
    }
    try {
      await axios.post(`${API}/subscription/cancel`);
      toast.success("Suscripción cancelada");
      setTimeout(() => {
        logout();
        navigate("/");
      }, 1000);
    } catch (error) {
      toast.error("Error al cancelar suscripción");
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-24">
        <h1 className="text-4xl md:text-5xl font-black text-white mb-8">Mis Datos</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-card/50 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Información Personal</CardTitle>
                <CardDescription className="text-white/60">
                  Tu información de perfil
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3 text-white">
                  <User className="w-5 h-5 text-[#E50914]" />
                  <div>
                    <p className="text-sm text-white/60">Nombre Completo</p>
                    <p className="font-medium">{user?.full_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-white">
                  <Mail className="w-5 h-5 text-[#E50914]" />
                  <div>
                    <p className="text-sm text-white/60">Email</p>
                    <p className="font-medium">{user?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-white">
                  <MapPin className="w-5 h-5 text-[#E50914]" />
                  <div>
                    <p className="text-sm text-white/60">País</p>
                    <p className="font-medium">{user?.country}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-white">
                  <Calendar className="w-5 h-5 text-[#E50914]" />
                  <div>
                    <p className="text-sm text-white/60">Miembro desde</p>
                    <p className="font-medium">
                      {user?.created_at
                        ? new Date(user.created_at).toLocaleDateString("es-ES", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                        : "—"}
                    </p>
                  </div>
                </div>

                {/* Cédula / Pasaporte */}
                <div className={`border-t pt-4 mt-2 ${!user?.id_number ? 'border-red-500/40' : 'border-white/10'}`}>
                  <Label htmlFor="id-number" className="text-white/80 flex items-center gap-2 mb-2">
                    <IdCard className="w-4 h-4 text-[#E50914]" />
                    Cédula / Pasaporte
                    <span className="text-xs text-[#E50914] font-bold">
                      * OBLIGATORIO para certificados
                    </span>
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="id-number"
                      data-testid="id-number-input"
                      value={idNumber}
                      onChange={(e) => setIdNumber(e.target.value)}
                      placeholder="Ej: 1712345678"
                      className={`bg-white/5 text-white placeholder:text-white/40 ${
                        !user?.id_number
                          ? 'border-red-500/60 focus-visible:ring-red-500/40'
                          : 'border-white/20'
                      }`}
                    />
                    <Button
                      onClick={handleSaveIdNumber}
                      disabled={savingId || idNumber === (user?.id_number || "")}
                      className="bg-[#E50914] hover:bg-[#E50914]/90 text-white"
                      data-testid="save-id-btn"
                    >
                      {savingId ? "Guardando..." : user?.id_number ? "Actualizar" : "Guardar"}
                    </Button>
                  </div>
                  {user?.id_number ? (
                    <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
                      ✓ Registrado: {user.id_number}
                    </p>
                  ) : (
                    <p className="text-xs text-red-400 mt-2 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Debes registrar tu cédula para poder descargar certificados
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Certificates */}
            <Card className="bg-card/50 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#E50914]" />
                  Mis Certificados
                </CardTitle>
                <CardDescription className="text-white/60">
                  Descarga tus certificados de asistencia
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!user?.id_number && certificates.length > 0 && (
                  <div
                    className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/40 flex items-start gap-3"
                    data-testid="id-required-banner"
                  >
                    <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-red-300 font-semibold text-sm">
                        Identificación requerida
                      </p>
                      <p className="text-red-200/80 text-xs mt-1">
                        Para descargar tus certificados primero debes ingresar tu cédula o pasaporte arriba.
                      </p>
                    </div>
                  </div>
                )}

                {loading ? (
                  <p className="text-white/60 text-center py-4">Cargando...</p>
                ) : certificates.length === 0 ? (
                  <div className="text-center py-8">
                    <Award className="w-12 h-12 text-white/20 mx-auto mb-3" />
                    <p className="text-white/60">Aún no has completado ningún curso</p>
                    <p className="text-white/40 text-sm mt-1">
                      Completa todas las lecciones de un curso para recibir tu certificado.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {certificates.map((cert) => (
                      <div
                        key={cert.course_id}
                        className="flex items-center justify-between gap-3 p-4 bg-white/5 rounded-lg border border-white/10 hover:border-[#E50914]/40 transition-all"
                        data-testid={`certificate-${cert.course_id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-white font-semibold truncate">{cert.course_title}</p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                            {cert.code && (
                              <span className="text-[#E50914] text-xs font-mono font-bold">
                                {cert.code}
                              </span>
                            )}
                            <span className="text-white/50 text-xs">
                              {cert.issued
                                ? `Emitido: ${new Date(cert.issued_at).toLocaleDateString("es-ES")}`
                                : `Completado: ${new Date(cert.issued_at).toLocaleDateString("es-ES")}`}
                            </span>
                            {!cert.issued && (
                              <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-xs">
                                Pendiente de emisión
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {cert.code && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-white/60 hover:text-white hover:bg-white/10"
                              onClick={() => window.open(cert.verify_url, "_blank")}
                              title="Verificar certificado"
                              data-testid={`verify-cert-${cert.course_id}`}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            disabled={downloading === cert.course_id || !user?.id_number}
                            className="bg-[#E50914] hover:bg-[#E50914]/90 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                            onClick={() => handleDownloadCertificate(cert)}
                            title={!user?.id_number ? "Primero registra tu cédula arriba" : "Descargar PDF"}
                            data-testid={`download-cert-${cert.course_id}`}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            {downloading === cert.course_id ? "..." : "PDF"}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Subscription Status */}
          <div>
            <Card className="bg-card/50 backdrop-blur-md border-white/10 sticky top-24">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Crown className="w-5 h-5 text-[#E50914]" />
                  Suscripción
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <Badge
                    className={
                      user?.subscription_status === "pro"
                        ? "bg-[#E50914] text-white border-0 text-lg px-4 py-2"
                        : "bg-white/10 text-white border-white/20 text-lg px-4 py-2"
                    }
                  >
                    {user?.subscription_status === "pro" ? "PRO" : "GRATIS"}
                  </Badge>
                </div>

                {user?.subscription_status === "pro" ? (
                  <>
                    <div className="text-center text-white/80 text-sm">
                      <p>Tu suscripción PRO está activa</p>
                      {user?.subscription_end_date && (
                        <p className="text-white/60 mt-2">
                          Válida hasta:{" "}
                          {new Date(user.subscription_end_date).toLocaleDateString("es-ES")}
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={handleCancelSubscription}
                      variant="outline"
                      className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                      data-testid="cancel-subscription-btn"
                    >
                      Cancelar Suscripción
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-white/60 text-sm text-center">
                      Actualiza a PRO para acceder a cursos exclusivos y certificados ilimitados
                    </p>
                    <Button
                      onClick={() => navigate("/contaflix-pro")}
                      className="w-full bg-[#E50914] hover:bg-[#E50914]/90 text-white shadow-[0_0_20px_rgba(229,9,20,0.4)] hover:shadow-[0_0_30px_rgba(229,9,20,0.6)] transition-all duration-300 font-semibold"
                      data-testid="upgrade-to-pro-btn"
                    >
                      Actualizar a PRO
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MyProfile;
